import { useCallback, useState } from 'react';
import { useStytch } from '@stytch/nextjs';
import axios from 'axios';

import GenericModal from '../modals/GenericModal';
import Button from 'components/app-components/ui/button';
import { SESSION_DURATION_MINUTES } from 'pages/authenticate';
import { logError } from 'utils/error';
import theraToast from 'components/generic-components/toast/TheraToast';
import { updateMFAUserDetails } from 'services/companyApi';

export const MFARecoveryCodeVerification = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const stytch = useStytch();
  const [recoveryCode, setRecoveryCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const recover = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsSubmitting(true);
      try {
        const res = await stytch.totps.recover({
          recovery_code: recoveryCode,
          session_duration_minutes: SESSION_DURATION_MINUTES,
        });
        // Delete the TOTP using our API endpoint
        await axios
          .delete('/api/mfa/delete-totp', {
            data: {
              totpId: res.totp_id,
            },
          })
          .catch((error) => {
            logError(error, { source: 'MFARecoveryCodeVerification' });
          });
        // Update 2FA type to email
        await updateMFAUserDetails(res.user_id, {
          mfaUserDetails: {
            mfaType: 'EMAIL',
          },
        });
        theraToast.success('Your account has been recovered. Please set up 2FA method again in your account settings.');
        setTimeout(() => {
          console.info('redirecting to home');
          window.location.reload();
        }, 2000);
      } catch (error) {
        setIsSubmitting(false);
        theraToast.error({
          message: 'Failed to recover your account. Please try again.',
          error: error,
          options: {
            id: 'failed-to-recover-account',
          },
        });
      }
    },
    [stytch, recoveryCode],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRecoveryCode(e.target.value);
  }, []);

  return (
    <GenericModal
      title="Recover your account"
      width="w-[30rem]"
      open={open}
      onClose={onClose}
      dialogClass="relative z-50"
    >
      <div className="mb-4 text-sm text-neutral-500">
        <p>
          If you have lost your recovery code, please contact our support team at{' '}
          <a href="mailto:support@getthera.com" className="underline">
            support@getthera.com
          </a>
        </p>
      </div>
      <form onSubmit={recover}>
        <div>
          <label htmlFor="recovery-code" className="form-label">
            Enter your saved recovery code
          </label>
          <input
            id="recovery-code"
            type="text"
            value={recoveryCode}
            onChange={handleChange}
            placeholder="Your 2FA recovery code"
            className="form-input w-full"
          />
        </div>
        <div className="mt-4 flex items-center justify-end">
          <Button htmlType="submit" loading={isSubmitting} disabled={!recoveryCode || isSubmitting}>
            Recover
          </Button>
        </div>
      </form>
    </GenericModal>
  );
};
